import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { api } from '../contexts/ApiContext';

export type PortalCredentialsSupplier = {
  id: string;
  name: string;
};

type Props = {
  supplier: PortalCredentialsSupplier | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

/**
 * Store / reveal invoice portal username+password for a supplier.
 * Password is write-only on list APIs; reveal uses a one-shot admin endpoint.
 */
export const SupplierPortalCredentialsDialog: React.FC<Props> = ({
  supplier,
  open,
  onClose,
  onSaved,
}) => {
  const [username, setUsername] = useState('');
  const [passwordDraft, setPasswordDraft] = useState('');
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [pluginKey, setPluginKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    if (!open || !supplier) return;
    let cancelled = false;
    const load = async () => {
      setError('');
      setInfo('');
      setPasswordDraft('');
      setRevealedPassword(null);
      setShowPassword(false);
      try {
        const res = await api.get(`/api/suppliers/${supplier.id}`);
        const row = res.data?.supplier || {};
        if (cancelled) return;
        setUsername(String(row.invoice_portal_username || ''));
        setHasPassword(Boolean(row.has_invoice_portal_password));
        setInvoiceUrl(String(row.invoice_url || ''));
        setPluginKey(String(row.invoice_fetch_plugin_key || ''));
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Failed to load supplier');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, supplier]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setInfo(`${label} copied`);
    } catch {
      setError(`Could not copy ${label}`);
    }
  };

  const revealPassword = async () => {
    if (!supplier) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const res = await api.get(`/api/suppliers/${supplier.id}/invoice-portal-credentials`);
      const pw = res.data?.invoice_portal_password;
      setRevealedPassword(pw == null ? null : String(pw));
      setHasPassword(Boolean(res.data?.has_invoice_portal_password));
      if (res.data?.invoice_portal_username != null) {
        setUsername(String(res.data.invoice_portal_username || ''));
      }
      if (!res.data?.has_invoice_portal_password) {
        setInfo('No password stored for this supplier yet.');
      } else {
        setShowPassword(true);
        setInfo('Password revealed (admin only).');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to reveal password');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!supplier) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const payload: Record<string, unknown> = {
        invoice_portal_username: username.trim() || null,
      };
      if (passwordDraft.trim()) {
        payload.invoice_portal_password = passwordDraft;
      }
      await api.put(`/api/suppliers/${supplier.id}`, payload);
      setPasswordDraft('');
      setRevealedPassword(null);
      setHasPassword(Boolean(passwordDraft.trim()) || hasPassword);
      setInfo('Portal credentials saved (password encrypted at rest).');
      onSaved?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save credentials');
    } finally {
      setBusy(false);
    }
  };

  const displayPassword =
    passwordDraft !== '' ? passwordDraft : revealedPassword != null ? revealedPassword : '';

  return (
    <Dialog open={open && Boolean(supplier)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Portal login — {supplier?.name || ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Store the portal username/email and password here (encrypted). Three Ireland
            can log in with these credentials. Cursor, Vodafone, Netflix, and PriceLabs
            still need a Playwright session export if they use OTP/OAuth. Password is
            never returned on normal supplier list APIs; use Reveal.
          </Typography>
          {pluginKey ? (
            <Typography variant="caption" color="text.secondary">
              Plugin: {pluginKey}
              {invoiceUrl ? ` · ${invoiceUrl}` : ''}
            </Typography>
          ) : invoiceUrl ? (
            <Typography variant="caption" color="text.secondary">
              {invoiceUrl}
            </Typography>
          ) : null}
          <TextField
            label="Portal username / email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: username ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Copy username"
                    onClick={() => void copyText(username, 'Username')}
                    edge="end"
                    size="small"
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <TextField
            label={
              passwordDraft
                ? 'New password (will replace stored)'
                : hasPassword
                  ? 'Password (reveal or enter new)'
                  : 'Password'
            }
            type={showPassword ? 'text' : 'password'}
            value={displayPassword}
            onChange={(e) => {
              setPasswordDraft(e.target.value);
              setRevealedPassword(null);
            }}
            size="small"
            fullWidth
            placeholder={hasPassword && !passwordDraft && revealedPassword == null ? '••••••••' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                  {displayPassword ? (
                    <IconButton
                      aria-label="Copy password"
                      onClick={() => void copyText(displayPassword, 'Password')}
                      edge="end"
                      size="small"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
          {info ? <Alert severity="info">{info}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button onClick={() => void revealPassword()} disabled={busy || !supplier}>
          Reveal password
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy || !supplier}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
